// ---------------------------------------------------------------------------
// The Assessment setup page — /assessment (NDI-239 v2)
//
// A customer's calculator (Google Sheet link or .xlsx) becomes a short
// script, checked against the workbook in 3 scenarios; their PDF template
// gets its placeholders mapped; one example is made and approved. Five
// steps — Calculator · Inputs · Check · Template · Example — in a
// micro-stepper, one card per step, one main action per card, few words.
//
// <sdr-assessment-page>, a LitElement in the light DOM (the app's stylesheet
// reaches it); AssessmentPage() places it, as the router does. Its timers are
// ReactiveControllers that end with it: the poll while a translation runs
// (every 2 s; it stops when the translation ends or the page is left) and
// the short messages that toasts used to carry.
//
// Which step is done, open or blocked comes from the server (`setup.steps`);
// setup-model.ts only reads it. Files go up as base64 JSON (the server has no
// multipart), checked here first for type and size. Text from the server —
// reasons, labels, the hook — is German and is marked lang="de"; the page
// itself is English like the app.
//
// Review fixes (R30–R37): why a step is not done is written on its card, and
// why Next is blocked next to it — never only in a tooltip on a disabled
// control. A server refusal stays on the card (role=alert, lang="de") until
// the next action. Every write goes through write(), which asks before a
// change that removes an approval (api.ts APPROVAL_EFFECT) — at the top of
// the card, in two buttons (Remove approval, Cancel), the rest of the card
// dimmed while it waits. An input change waiting to be saved is kept when a
// poll or another answer arrives, and is saved before another setup opens.
//
// Visual polish (after the v2 screenshot review): the inputs are one aligned
// table; numbers are German with their unit, as the customer's PDF prints
// them; a passed check is one line with the details folded; an approved
// setup is a calm summary with "Change example"; a click on a blocked step
// says why under the stepper; server warnings look like warnings.
//
// Shown only with the `assessmentAgent` flag. No Adornis, no baseQL, no
// router, no toast (it needs @adornis/chemistry): the DOM suite renders this
// page (tests/dom/assessment-setup.dom.ts), and the ratchet follows its
// imports. Colours only from the theme's variables (look.ts).
// ---------------------------------------------------------------------------

import { html, LitElement, nothing, type TemplateResult } from 'lit';
import { keyed } from 'lit/directives/keyed.js';
import { live as liveValue } from 'lit/directives/live.js';

import { toneDot } from '../campaign-status.js';
import { icon } from '../design/icons.js';
import {
  API,
  ApiError,
  callApi,
  type Endpoint,
  messageOf,
  revokesApproval,
  type SetupInput,
  type SetupView,
  urlOf,
} from './api.js';
import { Notice, Poller } from './controllers.js';
import { hasFeature } from './features.js';
import {
  buttonClass,
  CARD_CLASS,
  CHECKBOX_CLASS,
  FIELD_CLASS,
  FILE_CLASS,
  FOLD_CLASS,
  foldSummary,
  SEGMENT_CLASS,
  SEGMENTS_CLASS,
  spinner,
  TEXTAREA_CLASS,
  WARNING_CLASS,
} from './look.js';
import { microStepper } from './micro-stepper.js';
import {
  approvalLook,
  bytesToBase64,
  checkUpload,
  fillsByName,
  firstOpenStep,
  formatNumber,
  labelKeepingUnit,
  labelWithoutUnit,
  mismatchedRows,
  placeholderChips,
  rangeProblem,
  readSteps,
  reasonsBesideTable,
  SETUP_STEPS,
  type StepKey,
  scenarioSummary,
  scenarioTable,
  stepperItems,
  unitOf,
  valueRefOptions,
} from './setup-model.js';

/** How long the Inputs step waits after the last change before it saves. */
const FIELDS_SAVE_DELAY_MS = 600;

const HELP: Record<StepKey, string> = {
  calculator: 'Link a Google Sheet or upload the Excel file.',
  fields: 'Where each input comes from, and what is plausible.',
  check: 'We translate the sheet into a script and compare 3 scenarios.',
  template: 'Your PDF with {{placeholders}}.',
  example: 'Try it once, then approve.',
};

type Mode = 'link' | 'upload';
type Steps = ReturnType<typeof readSteps>;

/** A file's bytes; FileReader where Blob.arrayBuffer is missing. */
function readBytes(file: File): Promise<Uint8Array> {
  if (typeof file.arrayBuffer === 'function') return file.arrayBuffer().then(b => new Uint8Array(b));
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(new Uint8Array(reader.result as ArrayBuffer));
    reader.onerror = () => reject(reader.error ?? new Error('The file could not be read.'));
    reader.readAsArrayBuffer(file);
  });
}

/** A number typed into a field, or null when it is none. */
function numberFrom(value: string): number | null {
  const trimmed = value.trim();
  if (!trimmed) return null;
  const n = Number(trimmed.replace(',', '.'));
  return Number.isFinite(n) ? n : null;
}

const sourceValue = (input: SetupInput): string =>
  input.source?.kind === 'research' ? `research:${input.source.recipe}` : 'assume';

/** Thrown when the user declines a change that would remove the approval — nothing is sent, nothing is shown. */
class Declined extends Error {}

/** What the page says before a change removes the approval (R33). */
const REVOKE_TITLE = 'Remove the approval?';
const REVOKE_TEXT = 'New assessments wait until you approve a new example.';
const REVOKE_IF_CHANGED_TEXT = `If the file changed, the approval is removed. ${REVOKE_TEXT}`;

/** Why the Inputs step cannot be left while a typed range is wrong: it is only on screen, not saved. */
const UNSAVED_RANGE = 'Fix the range first — it is not saved yet.';

/** A question on the card, answered with Continue or Cancel. */
interface Ask {
  text: string;
  answer: (ok: boolean) => void;
}

export class SdrAssessmentPage extends LitElement {
  static override properties = {
    pollMs: { attribute: false },
    enabled: { state: true },
    setups: { state: true },
    setup: { state: true },
    step: { state: true },
    busy: { state: true },
    loadError: { state: true },
    calcMode: { state: true },
    calcLink: { state: true },
    calcReplace: { state: true },
    tplMode: { state: true },
    tplLink: { state: true },
    tplReplace: { state: true },
    fileError: { state: true },
    checkNote: { state: true },
    exampleDraft: { state: true },
    hookDraft: { state: true },
    notRight: { state: true },
    feedback: { state: true },
    rangeDrafts: { state: true },
    fieldsError: { state: true },
    refusal: { state: true },
    fieldsEpoch: { state: true },
    asking: { state: true },
    editingApproved: { state: true },
  };

  declare pollMs: number;
  protected declare enabled: boolean | null;
  protected declare setups: SetupView[] | null;
  protected declare setup: SetupView | null;
  protected declare step: StepKey;
  protected declare busy: string;
  protected declare loadError: string;
  protected declare calcMode: Mode;
  protected declare calcLink: string;
  protected declare calcReplace: boolean;
  protected declare tplMode: Mode;
  protected declare tplLink: string;
  protected declare tplReplace: boolean;
  protected declare fileError: string;
  protected declare checkNote: string;
  protected declare exampleDraft: Record<string, string>;
  protected declare hookDraft: string | null;
  protected declare notRight: boolean;
  protected declare feedback: string;
  /** A range being typed, per input key, while it is not one the server takes — it stays here, unsent. */
  protected declare rangeDrafts: Record<string, { min: string; max: string; problem: string }>;
  /** German, from the server: why the last save of the inputs was refused — one line, not a message per keystroke. */
  protected declare fieldsError: string;
  /** The last action's refusal, on the card until the next action (R34); German when the server said it. */
  protected declare refusal: { text: string; german: boolean } | null;
  /** Bumped to draw the Inputs card afresh — after a declined change, so its fields show what is saved again. */
  protected declare fieldsEpoch: number;
  /** The question before a change removes the approval, while it waits for an answer (R33). */
  protected declare asking: Ask | null;
  /** An approved setup shows a calm summary; "Change example" opens the example's form again. */
  protected declare editingApproved: boolean;

  private readonly poller = new Poller(this);
  private readonly notice = new Notice(this);
  /** Why a blocked step did not open, under the stepper for a few seconds — after a click or a tap on it. */
  private readonly stepNotice = new Notice(this);
  /** The name of the blocked step that stepNotice explains. */
  private blockedStep = { label: '', german: false };

  /** What handlers and timers share between renders and answers. */
  private readonly live = {
    alive: false,
    busy: '',
    polling: false,
    fieldsTimer: undefined as ReturnType<typeof setTimeout> | undefined,
    fieldsSeq: 0,
    /** An input change not yet answered by the server: answers in between keep it (R32). */
    fieldsDirty: false,
    /**
     * The setup whose approval the user agreed to give up for the input change now waiting to be saved — so typing it
     * asks once, not per keystroke. It counts only while that change waits (fieldsDirty); every other change asks
     * again while the server still says approved (a Refresh that found nothing, a refused change: N2).
     */
    revokeOkFor: '',
  };

  constructor() {
    super();
    this.pollMs = 2000;
    this.enabled = null;
    this.setups = null;
    this.setup = null;
    this.step = 'calculator';
    this.busy = '';
    this.loadError = '';
    this.calcMode = 'link';
    this.calcLink = '';
    this.calcReplace = false;
    this.tplMode = 'link';
    this.tplLink = '';
    this.tplReplace = false;
    this.fileError = '';
    this.checkNote = '';
    this.exampleDraft = {};
    this.hookDraft = null;
    this.notRight = false;
    this.feedback = '';
    this.rangeDrafts = {};
    this.fieldsError = '';
    this.refusal = null;
    this.fieldsEpoch = 0;
    this.asking = null;
    this.editingApproved = false;
  }

  override createRenderRoot() {
    return this;
  }

  // ── Mount: the flag, then the setups ──────────────────────────────────
  override connectedCallback() {
    super.connectedCallback();
    if (!this.style.display) this.style.display = 'block';
    this.live.alive = true;
    this.busy = '';
    this.live.busy = '';
    void (async () => {
      const on = await hasFeature('assessmentAgent');
      if (!this.live.alive) return;
      this.enabled = on;
      if (on) await this.loadSetups();
    })();
  }

  override disconnectedCallback() {
    super.disconnectedCallback();
    this.live.alive = false;
    this.live.polling = false;
    this.answer(false);
    // A change typed just before leaving is still saved.
    if (this.live.fieldsTimer !== undefined) {
      clearTimeout(this.live.fieldsTimer);
      void this.saveFields();
    }
  }

  // ── Polling while the translation runs ────────────────────────────────
  protected override updated() {
    const id = this.setup?._id ?? '';
    const running = this.setup?.conversion?.status === 'running';
    this.poller.run(running && id ? `${id}:${this.pollMs}` : '', this.pollMs, () => void this.refresh(id));
  }

  /** Shows `next` (the server's answer); `fresh` when another setup was chosen — the drafts start over. */
  private applySetup(next: SetupView | null, fresh = false) {
    if (!this.live.alive) return;
    this.setup = next;
    if (next && this.setups) {
      this.setups = this.setups.some(s => s._id === next._id)
        ? this.setups.map(s => (s._id === next._id ? next : s))
        : [...this.setups, next];
    }
    if (fresh) {
      this.step = next ? firstOpenStep(readSteps(next.steps)) : 'calculator';
      this.exampleDraft = {};
      this.hookDraft = null;
      this.notRight = false;
      this.feedback = '';
      this.checkNote = '';
      this.calcReplace = false;
      this.tplReplace = false;
      this.fileError = '';
      this.rangeDrafts = {};
      this.fieldsError = '';
      this.refusal = null;
      this.editingApproved = false;
      this.notice.clear();
      this.stepNotice.clear();
      this.answer(false);
      this.live.fieldsDirty = false;
      this.live.revokeOkFor = '';
    }
  }

  /**
   * Shows a setup the server sent (a poll, an action's answer). While an input change waits to be saved, the page's
   * inputs and outputs win: the server's copy does not have the change yet, and the save that follows would send it
   * back (R32).
   */
  private applyServer(next: SetupView) {
    const current = this.setup;
    if (this.live.fieldsDirty && current && next._id === current._id) {
      this.applySetup({ ...next, inputs: current.inputs, outputs: current.outputs });
    } else this.applySetup(next);
  }

  /**
   * Opens a step; a half-done Replace, an open question, an opened approved example and the last refusal belong to the
   * step that is left.
   */
  private goTo(key: StepKey) {
    this.step = key;
    this.calcReplace = false;
    this.tplReplace = false;
    this.fileError = '';
    this.refusal = null;
    this.editingApproved = false;
    this.notice.clear();
    this.stepNotice.clear();
    this.answer(false);
  }

  /** Answers the open question, if there is one. */
  private answer(ok: boolean) {
    const open = this.asking;
    if (!open) return;
    this.asking = null;
    open.answer(ok);
  }

  /** Asks on the card; a question still open is answered Cancel first. */
  private ask(text: string): Promise<boolean> {
    this.answer(false);
    if (!this.live.alive) return Promise.resolve(false);
    return new Promise<boolean>(resolve => {
      const before = document.activeElement as HTMLElement | null;
      this.asking = {
        text,
        answer: ok => {
          resolve(ok);
          // Back where the user was, as a dialog would; only if the question had taken the focus.
          if (before && this.contains(document.activeElement) && document.activeElement?.closest('[data-ask]')) {
            try {
              before.focus();
            } catch {
              // An element gone from the page cannot take the focus back; nothing depends on it.
            }
          }
        },
      };
      void this.updateComplete.then(() => this.querySelector<HTMLButtonElement>('[data-ask] [data-continue]')?.focus());
    });
  }

  /**
   * Whether a change of the current setup may go ahead: yes unless it is approved — then the user is asked, every time
   * the server still says approved, since the server takes the approval back and leads cannot start new assessments
   * until a new example is approved (R33, N2).
   */
  private async mayChangeApproved(text = REVOKE_TEXT): Promise<boolean> {
    const current = this.setup;
    if (!current || !this.mustAsk(current)) return true;
    return this.ask(text);
  }

  /**
   * A change of this setup removes an approval the user has not yet agreed to give up: whenever the server's last word
   * is "approved" — unless the change is part of the input change the user already agreed to, which still waits (N2).
   */
  private mustAsk(s: SetupView): boolean {
    return s.approval?.status === 'approved' && !(this.live.fieldsDirty && this.live.revokeOkFor === s._id);
  }

  /** The one way the page writes to a setup: asks first when the endpoint removes an approval (APPROVAL_EFFECT). */
  private async write<T>(endpoint: Endpoint, id: string, body?: unknown): Promise<T> {
    if (revokesApproval(endpoint)) {
      const refresh = endpoint === API.refreshCalculator || endpoint === API.refreshTemplate;
      if (!(await this.mayChangeApproved(refresh ? REVOKE_IF_CHANGED_TEXT : REVOKE_TEXT))) throw new Declined();
    }
    return callApi<T>(endpoint, { id, body });
  }

  private async loadSetups() {
    try {
      const data = await callApi<{ setups?: SetupView[] }>(API.listSetups);
      if (!this.live.alive) return;
      const list = Array.isArray(data.setups) ? data.setups : [];
      this.setups = list;
      this.loadError = '';
      this.applySetup(list[0] ?? null, true);
    } catch (err) {
      if (this.live.alive) this.loadError = messageOf(err);
    }
  }

  private async refresh(id: string) {
    if (this.live.polling) return;
    this.live.polling = true;
    try {
      const data = await callApi<{ setup: SetupView }>(API.getSetup, { id });
      if (this.live.alive && this.setup?._id === id) this.applyServer(data.setup);
    } catch (err) {
      // A missed poll is retried by the next one.
      console.warn('[assessment] polling the setup failed', err);
    } finally {
      this.live.polling = false;
    }
  }

  /**
   * Runs one action at a time. A refusal stays on the card in the server's (German) words until the next action — the
   * user has to act on it (R34). A declined question is no refusal.
   */
  private async act(name: string, work: () => Promise<void>) {
    if (this.live.busy) return;
    this.live.busy = name;
    this.busy = name;
    this.refusal = null;
    this.notice.clear();
    try {
      await work();
    } catch (err) {
      if (this.live.alive && !(err instanceof Declined)) {
        const german = err instanceof ApiError && typeof err.body.error === 'string' && !!err.body.error;
        this.refusal = { text: messageOf(err), german };
      }
    } finally {
      this.live.busy = '';
      if (this.live.alive) this.busy = '';
    }
  }

  /** Sends one request about the current setup and shows the setup it answers with. */
  private async send(endpoint: Endpoint, body?: unknown): Promise<Record<string, unknown>> {
    const current = this.setup;
    if (!current) throw new Error('No setup selected.');
    const data = await this.write<Record<string, unknown>>(endpoint, current._id, body);
    if (data.setup && typeof data.setup === 'object' && this.setup?._id === current._id) {
      this.applyServer(data.setup as SetupView);
    }
    return data;
  }

  // ── Fields: saved a moment after the last change ──────────────────────
  private async saveFields() {
    this.live.fieldsTimer = undefined;
    const current = this.setup;
    if (!current) return;
    const seq = this.live.fieldsSeq;
    try {
      const data = await this.write<{ setup?: SetupView }>(API.putFields, current._id, {
        inputs: (current.inputs ?? []).map(i => ({
          key: i.key,
          example: i.example,
          source: i.source,
          // null removes it — an input switched to research keeps no hidden assumption (R30).
          ...(i.default !== undefined ? { default: i.default } : {}),
          ...(i.plausible ? { plausible: i.plausible } : {}),
        })),
        outputs: (current.outputs ?? []).map(o => ({ key: o.key, keep: o.keep })),
      });
      // A change made while this was on its way wins; its own save follows.
      if (seq === this.live.fieldsSeq) this.settleFields();
      if (data.setup && seq === this.live.fieldsSeq && this.setup?._id === current._id) this.applySetup(data.setup);
      if (this.live.alive && seq === this.live.fieldsSeq) this.fieldsError = '';
    } catch (err) {
      if (seq === this.live.fieldsSeq) this.settleFields();
      if (this.live.alive && this.setup?._id === current._id) this.fieldsError = messageOf(err);
    }
  }

  /** The waiting input change is answered: the agreement given for it ends with it (N2). */
  private settleFields() {
    this.live.fieldsDirty = false;
    this.live.revokeOkFor = '';
  }

  /** Saves a waiting input change now — before another setup is shown, so it reaches the setup it was made in. */
  private flushFields() {
    if (this.live.fieldsTimer === undefined) return;
    clearTimeout(this.live.fieldsTimer);
    void this.saveFields();
  }

  /**
   * An input change: shown at once, saved a moment later. On an approved setup the user is asked first; the change is
   * applied only to the setup it was made in, and a declined one leaves the fields as saved.
   */
  private changeFields(next: Pick<SetupView, 'inputs' | 'outputs'>) {
    const current = this.setup;
    if (!current) return;
    if (!this.mustAsk(current)) {
      this.applyFieldChange(next);
      return;
    }
    void this.mayChangeApproved().then(ok => {
      if (ok && this.setup?._id === current._id) {
        // Agreed for this input change: its further keystrokes and its save do not ask again.
        this.live.revokeOkFor = current._id;
        this.applyFieldChange(next);
      } else if (this.live.alive) {
        this.fieldsEpoch++;
      }
    });
  }

  private applyFieldChange(next: Pick<SetupView, 'inputs' | 'outputs'>) {
    const current = this.setup;
    if (!current) return;
    this.applySetup({ ...current, ...next });
    this.live.fieldsSeq++;
    this.live.fieldsDirty = true;
    clearTimeout(this.live.fieldsTimer);
    this.live.fieldsTimer = setTimeout(() => void this.saveFields(), FIELDS_SAVE_DELAY_MS);
  }

  private updateInput(key: string, patch: Partial<SetupInput>) {
    this.changeFields({ inputs: (this.setup?.inputs ?? []).map(i => (i.key === key ? { ...i, ...patch } : i)) });
  }

  /** One end of a range typed: saved when both ends make a range, else held here with a short hint. */
  private typeRange(input: SetupInput, end: 'min' | 'max', typed: string) {
    const current = this.setup?.inputs?.find(i => i.key === input.key) ?? input;
    const draft = this.rangeDrafts[input.key] ?? {
      min: String(current.plausible?.min ?? ''),
      max: String(current.plausible?.max ?? ''),
      problem: '',
    };
    const next = { ...draft, [end]: typed };
    const min = numberFrom(next.min);
    const max = numberFrom(next.max);
    const problem = rangeProblem(min, max);
    if (problem || min === null || max === null) {
      this.rangeDrafts = { ...this.rangeDrafts, [input.key]: { ...next, problem: problem ?? '' } };
      return;
    }
    const { [input.key]: _done, ...rest } = this.rangeDrafts;
    this.rangeDrafts = rest;
    this.updateInput(input.key, { plausible: { min, max } });
  }

  /** A range is typed but not saved (it is not a range yet): the Inputs step is not left until it is fixed. */
  private hasUnsavedRange(): boolean {
    return Object.keys(this.rangeDrafts).length > 0;
  }

  /** Opens another setup; a change still waiting is saved to the one it was made in first (R32). */
  private chooseSetup(id: string) {
    this.flushFields();
    this.applySetup((this.setups ?? []).find(s => s._id === id) ?? null, true);
  }

  // ── Actions ───────────────────────────────────────────────────────────
  private readonly create = (fromSample: boolean) =>
    this.act(fromSample ? 'sample' : 'scratch', async () => {
      const data = await callApi<{ setup: SetupView }>(API.createSetup, {
        body: fromSample ? { fromSample: true } : {},
      });
      if (!this.live.alive) return;
      this.setups = [...(this.setups ?? []), data.setup];
      this.applySetup(data.setup, true);
    });

  private readonly convert = (note?: string) =>
    this.act('convert', async () => {
      const text = note?.trim();
      const data = await this.send(API.convert, text ? { feedback: text } : {});
      // 202 without a body: show it running now; the poll brings the rest.
      if (!data.setup && this.setup) {
        this.applySetup({ ...this.setup, conversion: { status: 'running', ...(text ? { feedback: text } : {}) } });
      }
      this.checkNote = '';
    });

  private uploadFile(kind: 'calculator' | 'template', event: Event) {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) return;
    // Choosing the same file again (after a refusal) must fire change again.
    try {
      input.value = '';
    } catch {
      // Some environments refuse to reset a file input; nothing depends on it.
    }
    const problem = checkUpload(file, kind === 'calculator' ? 'xlsx' : 'pdf');
    if (problem) {
      this.fileError = problem;
      return;
    }
    this.fileError = '';
    void this.act(kind, async () => {
      const base64 = bytesToBase64(await readBytes(file));
      await this.send(kind === 'calculator' ? API.putCalculator : API.putTemplate, { fileName: file.name, base64 });
      if (kind === 'calculator') this.calcReplace = false;
      else this.tplReplace = false;
    });
  }

  private readonly loadLink = (kind: 'calculator' | 'template', url: string) =>
    this.act(kind, async () => {
      await this.send(kind === 'calculator' ? API.putCalculator : API.putTemplate, { url: url.trim() });
      if (kind === 'calculator') {
        this.calcReplace = false;
        this.calcLink = '';
      } else {
        this.tplReplace = false;
        this.tplLink = '';
      }
    });

  private readonly refreshLink = (kind: 'calculator' | 'template') =>
    this.act(`${kind}-refresh`, async () => {
      const data = await this.send(kind === 'calculator' ? API.refreshCalculator : API.refreshTemplate);
      this.notice.show(data.unchanged ? 'No changes since the last load.' : 'Updated.');
    });

  private async copyName(name: string) {
    const text = `{{${name}}}`;
    try {
      await navigator.clipboard.writeText(text);
      if (this.live.alive) this.notice.show(`Copied ${text}`);
    } catch {
      // No clipboard: the name stays on the card to be selected.
      if (this.live.alive) this.notice.show(text, 8000);
    }
  }

  private readonly createExample = () =>
    this.act('example', async () => {
      const current = this.setup;
      if (!current) return;
      const hookDraft = this.hookDraft;
      const original = current.hook?.templates?.[0] ?? '';
      if (hookDraft !== null && hookDraft.trim() && hookDraft.trim() !== original) {
        await this.send(API.putHook, { templates: [hookDraft.trim()] });
      }
      const inputs: Record<string, number> = {};
      for (const i of this.setup?.inputs ?? []) {
        const typed = this.exampleDraft[i.key];
        const value = typed !== undefined ? numberFrom(typed) : (this.setup?.example?.inputs?.[i.key] ?? i.example);
        if (value === null || value === undefined) throw new Error(`Enter a number for ${i.label}.`);
        inputs[i.key] = value;
      }
      await this.send(API.createExample, { inputs });
      // The example was made with the saved hook: the textarea shows that one now.
      this.hookDraft = null;
      this.notRight = false;
    });

  private readonly approve = () =>
    this.act('approve', async () => {
      await this.send(API.approve);
      // Approved again: no earlier agreement carries over to it. The card shows the calm summary now.
      this.live.revokeOkFor = '';
      this.editingApproved = false;
    });

  /**
   * "Not right": the note is saved with the setup (it shows again on the Example card), then the sheet is translated
   * again with it — one button instead of "Save note" and "Re-translate with this note".
   */
  private readonly retranslate = () =>
    this.act('convert', async () => {
      const note = this.feedback.trim();
      if (!note) return;
      await this.send(API.feedback, { text: note });
      const data = await this.send(API.convert, { feedback: note });
      if (!data.setup && this.setup) {
        this.applySetup({ ...this.setup, conversion: { status: 'running', feedback: note } });
      }
      if (this.setup?.conversion?.status === 'running') {
        this.notRight = false;
        this.feedback = '';
        this.goTo('check');
      }
    });

  // ── Pieces ────────────────────────────────────────────────────────────
  private spin(name: string) {
    return this.busy === name ? spinner() : nothing;
  }

  private modeSwitch(mode: Mode, set: (m: Mode) => void, labels: [string, string]) {
    return html`
      <div class=${SEGMENTS_CLASS} role="group">
        ${(['link', 'upload'] as const).map(
          (m, i) => html`<button
            type="button"
            class=${mode === m ? SEGMENT_CLASS.on : SEGMENT_CLASS.off}
            aria-pressed=${mode === m ? 'true' : 'false'}
            @click=${() => {
              set(m);
              this.fileError = '';
            }}
          >
            ${labels[i]}
          </button>`,
        )}
      </div>
    `;
  }

  /** Link or upload; while a loaded file is being replaced, `onCancel` keeps it (R36). */
  private sourcePicker(
    kind: 'calculator' | 'template',
    mode: Mode,
    link: string,
    setLink: (v: string) => void,
    onCancel?: () => void,
  ) {
    const isCalc = kind === 'calculator';
    const busy = !!this.busy;
    return html`
      <div class="flex flex-col gap-3">
        <div class="flex items-center justify-between gap-2">
          ${this.modeSwitch(
            mode,
            m => {
              if (isCalc) this.calcMode = m;
              else this.tplMode = m;
            },
            isCalc ? ['Google Sheet link', 'Upload Excel'] : ['Drive link', 'Upload PDF'],
          )}
          ${
            onCancel
              ? html`<button
                type="button"
                class=${buttonClass('quiet')}
                ?disabled=${busy}
                @click=${() => {
                  this.fileError = '';
                  onCancel();
                }}
              >
                Cancel
              </button>`
              : nothing
          }
        </div>
        ${
          mode === 'link'
            ? html`<form
              class="flex gap-2"
              @submit=${(e: Event) => {
                e.preventDefault();
                if (link.trim()) void this.loadLink(kind, link);
              }}
            >
              <input
                class="${FIELD_CLASS.sm} flex-1"
                type="url"
                name="${kind}-link"
                aria-label=${isCalc ? 'Google Sheet link' : 'Google Drive link'}
                placeholder=${isCalc ? 'Paste the Google Sheet link' : 'Paste the Google Drive link'}
                .value=${link}
                @input=${(e: Event) => setLink((e.target as HTMLInputElement).value)}
              />
              <button type="submit" class=${buttonClass('primary')} ?disabled=${!link.trim() || busy}>
                ${this.spin(kind)} Load
              </button>
            </form>`
            : html`<input
              type="file"
              class=${FILE_CLASS}
              accept=${isCalc ? '.xlsx' : '.pdf'}
              aria-label=${isCalc ? 'Excel file' : 'PDF template'}
              ?disabled=${busy}
              @change=${(e: Event) => this.uploadFile(kind, e)}
            />`
        }
        ${this.fileError ? html`<p class="text-sm text-(--color-error)" role="alert">${this.fileError}</p>` : nothing}
        ${
          this.busy === kind && mode === 'upload'
            ? html`<p class="flex items-center gap-2 text-sm text-(--sdr-ink-muted)">${this.spin(kind)} Uploading…</p>`
            : nothing
        }
      </div>
    `;
  }

  private loadedFile(
    kind: 'calculator' | 'template',
    file: { source: Mode; fileName: string; url?: string },
    onReplace: () => void,
  ) {
    const busy = !!this.busy;
    return html`
      <div class="flex flex-wrap items-center gap-3 rounded-lg border border-(--sdr-line) px-3 py-2">
        <span class="flex text-(--sdr-ink-subtle)">${icon({ name: 'document', size: 20 })}</span>
        <div class="min-w-0 flex-1">
          <div class="truncate text-sm font-medium">${file.fileName}</div>
          <div class="text-xs text-(--sdr-ink-subtle)">${file.source === 'link' ? 'From link' : 'Uploaded'}</div>
        </div>
        ${
          file.source === 'link'
            ? html`<button
              type="button"
              class=${buttonClass('quiet', 'xs')}
              ?disabled=${busy}
              @click=${() => void this.refreshLink(kind)}
            >
              ${this.busy === `${kind}-refresh` ? this.spin(`${kind}-refresh`) : icon({ name: 'sync', size: 14 })} Refresh
            </button>`
            : nothing
        }
        <button type="button" class=${buttonClass('quiet', 'xs')} ?disabled=${busy} @click=${onReplace}>Replace</button>
      </div>
    `;
  }

  /**
   * German lines from the server. `warning` (a workbook's or template's warnings) draws them as a warning — the amber
   * rule of stepProblem, no bullet — so they do not read as content; plain (a failed check's reasons) as a list.
   */
  private germanLines(lines: (string | undefined)[], tone: 'plain' | 'warning' = 'plain') {
    const all = lines
      .flatMap(l => (l ?? '').split('\n'))
      .map(l => l.trim())
      .filter(Boolean);
    if (!all.length) return nothing;
    if (tone === 'warning') {
      return html`<div class="${WARNING_CLASS} flex flex-col gap-1 text-sm" lang="de" data-warning>
        ${all.map(l => html`<p>${l}</p>`)}
      </div>`;
    }
    return html`<ul class="flex list-disc flex-col gap-1 pl-5 text-sm" lang="de">
      ${all.map(l => html`<li>${l}</li>`)}
    </ul>`;
  }

  private nextButton(from: StepKey, steps: Steps) {
    const at = SETUP_STEPS.findIndex(s => s.key === from);
    const next = SETUP_STEPS[at + 1];
    // While the approval question is open it is the one decision on the card: no second navy button beside it.
    if (!next || this.asking) return nothing;
    // A range on screen that is not saved would be left behind, unseen: the Check would run on the old one.
    const unsaved = from === 'fields' && this.hasUnsavedRange();
    const blocked = unsaved || steps[next.key].state === 'blocked';
    const reason = unsaved ? UNSAVED_RANGE : blocked ? steps[next.key].reason : undefined;
    // Why Next is blocked is written beside it: a disabled button is skipped by Tab and has no tooltip on touch (R37).
    return html`<span class="flex flex-wrap items-center justify-end gap-x-3 gap-y-1">
      ${
        reason
          ? html`<span id="assessment-next-why" class="text-xs text-(--sdr-ink-muted)" lang=${unsaved ? nothing : 'de'}
              >${reason}</span
            >`
          : nothing
      }
      <button
        type="button"
        class=${buttonClass('primary')}
        ?disabled=${blocked}
        aria-describedby=${reason ? 'assessment-next-why' : nothing}
        @click=${() => this.goTo(next.key)}
      >
        Next ${icon({ name: 'chevron-right', size: 16 })}
      </button>
    </span>`;
  }

  /** Why the shown step is not done — the server's German reason, on the card, not only in a tooltip (R30). */
  private stepProblem(reason: string | undefined) {
    return reason
      ? html`<p class="${WARNING_CLASS} text-sm" lang="de" data-step-problem>${reason}</p>`
      : nothing;
  }

  // ── The five cards ────────────────────────────────────────────────────
  private calculatorCard(s: SetupView, steps: Steps) {
    return html`
      ${
        s.calculator && !this.calcReplace
          ? html`${this.loadedFile('calculator', s.calculator, () => {
              this.calcReplace = true;
            })}
            ${this.germanLines(s.calculator.warnings ?? [], 'warning')}`
          : this.sourcePicker(
              'calculator',
              this.calcMode,
              this.calcLink,
              v => {
                this.calcLink = v;
              },
              s.calculator
                ? () => {
                    this.calcReplace = false;
                  }
                : undefined,
            )
      }
      ${s.calculator && !this.calcReplace ? html`<div class="flex justify-end">${this.nextButton('calculator', steps)}</div>` : nothing}
    `;
  }

  private fieldsCard(s: SetupView, steps: Steps) {
    return keyed(`${s._id}:${this.fieldsEpoch}`, this.fieldsBody(s, steps));
  }

  /**
   * The inputs as one aligned table — a header row (Source · Value · Range) and a row per input on the same columns,
   * so the Range fields line up whether or not an input has a value of its own. The example values are set in the
   * Example step only (they were edited twice). On a phone a row is three short lines: name, source (and value),
   * range.
   */
  private fieldsBody(s: SetupView, steps: Steps) {
    const drafts = this.rangeDrafts;
    const inputs = s.inputs ?? [];
    const outputs = s.outputs ?? [];
    const columns = 'grid-cols-2 sm:grid-cols-[minmax(0,1fr)_9rem_3.5rem_9rem]';
    const caption = 'text-xs text-(--sdr-ink-subtle)';
    return html`
      ${steps.fields.state === 'todo' ? this.stepProblem(steps.fields.reason) : nothing}
      ${
        inputs.length === 0
          ? html`<p class="text-sm text-(--sdr-ink-subtle)">No inputs found.</p>`
          : html`<div class="flex flex-col" data-inputs>
              <div class="hidden ${columns} gap-x-2 border-b border-(--sdr-line) pb-1.5 sm:grid" aria-hidden="true">
                <span></span>
                <span class=${caption}>Source</span>
                <span class=${caption}>Value</span>
                <span class=${caption}>Range</span>
              </div>
              ${inputs.map(i => {
                const source = sourceValue(i);
                const assume = source === 'assume';
                const draft = drafts[i.key];
                return html`<div
                  class="grid ${columns} items-center gap-x-2 gap-y-1.5 border-b border-(--sdr-line) py-2.5 last:border-0"
                  data-input=${i.key}
                >
                  <div class="col-span-2 min-w-0 sm:col-span-1">
                    <div class="text-sm" lang="de">${labelKeepingUnit(i.label)}</div>
                    ${i.cell ? html`<div class=${caption}>${i.cell}</div>` : nothing}
                  </div>
                  <select
                    class="${FIELD_CLASS.xs} w-full ${assume ? '' : 'col-span-2 sm:col-span-1'}"
                    aria-label="Source of ${i.label}"
                    @change=${(e: Event) => {
                      const v = (e.target as HTMLSelectElement).value;
                      this.updateInput(
                        i.key,
                        v === 'assume'
                          ? // An assumption needs its value; the field shows the example until one is typed — so send it.
                            { source: { kind: 'assume' }, default: i.default ?? i.example }
                          : {
                              source: { kind: 'research', recipe: v === 'research:students' ? 'students' : 'teachers' },
                              // Research shows no value field: an old assumption must not stay behind, unseen (R30).
                              ...(i.default !== undefined ? { default: null } : {}),
                            },
                      );
                    }}
                  >
                    <option value="research:teachers" ?selected=${source === 'research:teachers'}>Research: teachers</option>
                    <option value="research:students" ?selected=${source === 'research:students'}>Research: students</option>
                    <option value="assume" ?selected=${assume}>Assumption</option>
                  </select>
                  ${
                    assume
                      ? html`<label class="flex min-w-0 items-center gap-1.5">
                          <span class="${caption} sm:hidden">Value</span>
                          <input
                            class="${FIELD_CLASS.xs} w-full"
                            type="number"
                            step="any"
                            aria-label="Assumed value of ${i.label}"
                            .value=${String(i.default ?? i.example ?? '')}
                            @change=${(e: Event) => {
                              const n = numberFrom((e.target as HTMLInputElement).value);
                              if (n !== null) this.updateInput(i.key, { default: n });
                            }}
                          />
                        </label>`
                      : html`<span class="hidden sm:block" aria-hidden="true"></span>`
                  }
                  <div class="col-span-2 flex min-w-0 items-center gap-1.5 sm:col-span-1">
                    <span class="${caption} sm:hidden">Range</span>
                    ${(['min', 'max'] as const).map(
                      (end, n) => html`${n === 1 ? html`<span class="text-(--sdr-ink-subtle)" aria-hidden="true">–</span>` : nothing}<input
                          class="${FIELD_CLASS.xs} w-full"
                          type="number"
                          step="any"
                          aria-label="${end === 'min' ? 'Minimum' : 'Maximum'} of ${i.label}"
                          aria-invalid=${draft?.problem ? 'true' : 'false'}
                          .value=${draft?.[end] ?? String(i.plausible?.[end] ?? '')}
                          @change=${(e: Event) => this.typeRange(i, end, (e.target as HTMLInputElement).value)}
                        />`,
                    )}
                  </div>
                  ${
                    draft?.problem
                      ? html`<p class="col-span-2 text-xs text-(--color-error) sm:col-span-4 sm:text-right" data-range-hint>
                          ${draft.problem} Not saved yet.
                        </p>`
                      : nothing
                  }
                </div>`;
              })}
            </div>`
      }
      ${
        outputs.length === 0
          ? html`<p class="text-sm text-(--sdr-ink-subtle)">No results found.</p>`
          : html`<div class="flex flex-col" data-outputs>
              <div class="flex items-center justify-between gap-3 border-b border-(--sdr-line) pb-1.5">
                <h3 class=${caption}>Results</h3>
                <span class=${caption} aria-hidden="true">Keep</span>
              </div>
              ${outputs.map(o => {
                const value = s.example?.outputs?.[o.key];
                return html`<label class="flex items-center gap-3 border-b border-(--sdr-line) py-2 last:border-0">
                  <span class="flex-1 text-sm" lang="de">${labelWithoutUnit(o.label, unitOf(o))}</span>
                  ${value !== undefined ? html`<span class="text-sm text-(--sdr-ink-muted)">${formatNumber(value, o)}</span>` : nothing}
                  <input
                    type="checkbox"
                    class=${CHECKBOX_CLASS}
                    aria-label="Keep ${o.label}"
                    .checked=${!!o.keep}
                    @change=${(e: Event) =>
                      this.changeFields({
                        outputs: (this.setup?.outputs ?? []).map(x =>
                          x.key === o.key ? { ...x, keep: (e.target as HTMLInputElement).checked } : x,
                        ),
                      })}
                  />
                </label>`;
              })}
            </div>`
      }
      ${this.fieldsError ? html`<p class="text-sm text-(--color-error)" role="alert" lang="de">${this.fieldsError}</p>` : nothing}
      <div class="flex justify-end">${this.nextButton('fields', steps)}</div>
    `;
  }

  /**
   * The check. Passed: one calm line and Next; the numbers (a row per result, a column per scenario) and the script
   * are folded under Details. Failed: the server's reasons, only the rows that differ, a note and "Translate again".
   */
  private checkCard(s: SetupView, steps: Steps) {
    const busy = !!this.busy;
    if (s.conversion?.status === 'running') {
      return html`<p class="flex items-center gap-2 text-sm" role="status">${spinner('sm')} Translating and checking…</p>`;
    }
    const verification = s.script?.verification;
    const summary = scenarioSummary(verification);
    const th = 'border-b border-(--sdr-line) px-2 py-1.5 text-xs font-normal text-(--sdr-ink-subtle)';
    const td = 'border-b border-(--sdr-line) px-2 py-1.5';
    if (s.conversion?.status === 'failed' || (summary && !summary.passed)) {
      const differ = mismatchedRows(verification, s.outputs ?? []);
      // A „Szenario n: …“ reason says what the table's rows for n already show (with the numbers): said once.
      const reasons = reasonsBesideTable(verification?.reasons ?? [], differ);
      return html`
        <p class="flex items-center gap-2 text-sm">${toneDot({ tone: 'critical', label: summary?.text ?? 'Check failed' })}</p>
        ${this.germanLines([s.conversion?.reason, ...reasons])}
        ${
          differ.length
            ? html`<div class="overflow-x-auto" data-mismatches>
                <table class="w-full border-collapse text-sm">
                  <thead>
                    <tr>
                      <th class="${th} text-left">Scenario</th>
                      <th class="${th} text-left">Result</th>
                      <th class="${th} text-right">Excel</th>
                      <th class="${th} text-right">Script</th>
                    </tr>
                  </thead>
                  <tbody class="[&>tr:last-child>td]:border-0">
                    ${differ.map(
                      r => html`<tr>
                        <td class="${td} text-(--sdr-ink-muted)">${r.scenario}</td>
                        <td class=${td} lang="de">${r.label}</td>
                        <td class="${td} text-right whitespace-nowrap">${r.excel}</td>
                        <td class="${td} text-right whitespace-nowrap text-(--color-error)">${r.script}</td>
                      </tr>`,
                    )}
                  </tbody>
                </table>
              </div>`
            : nothing
        }
        <textarea
          class=${TEXTAREA_CLASS}
          name="check-note"
          rows="2"
          aria-label="Note for the translation"
          placeholder="Note for the translation (optional)"
          .value=${this.checkNote}
          @input=${(e: Event) => {
            this.checkNote = (e.target as HTMLTextAreaElement).value;
          }}
        ></textarea>
        <div class="flex justify-end">
          <button type="button" class=${buttonClass('primary')} ?disabled=${busy} @click=${() => void this.convert(this.checkNote)}>
            ${this.spin('convert')} Translate again
          </button>
        </div>
      `;
    }
    if (summary?.passed && s.script) {
      const table = scenarioTable(verification, s.outputs ?? []);
      return html`
        <p class="flex items-center gap-2 text-sm" data-check-passed>
          <span class="flex text-(--color-success)">${icon({ name: 'check', size: 18 })}</span>${summary.text}
        </p>
        <details class=${FOLD_CLASS}>
          ${foldSummary('Details')}
          <div class="mt-3 flex flex-col gap-3">
            <div class="overflow-x-auto">
              <table class="w-full border-collapse text-sm">
                <thead>
                  <tr>
                    <th class="${th} text-left">Result</th>
                    ${table.scenarios.map(
                      n => html`<th class="${th} text-right whitespace-nowrap"><span class="hidden sm:inline">Scenario </span>${n}</th>`,
                    )}
                  </tr>
                </thead>
                <tbody class="[&>tr:last-child>td]:border-0">
                  ${table.rows.map(
                    r => html`<tr>
                      <td class=${td} lang="de">${r.label}</td>
                      ${r.values.map(v => html`<td class="${td} text-right whitespace-nowrap">${v}</td>`)}
                    </tr>`,
                  )}
                </tbody>
              </table>
            </div>
            <pre class="max-h-80 overflow-auto rounded-lg bg-(--sdr-sunken) p-3 font-mono text-xs leading-5 whitespace-pre">${s.script.source}</pre>
          </div>
        </details>
        <div class="flex justify-end gap-2">
          <button type="button" class=${buttonClass('quiet')} ?disabled=${busy} @click=${() => void this.convert()}>
            ${this.spin('convert')} Translate again
          </button>
          ${this.nextButton('check', steps)}
        </div>
      `;
    }
    return html`<div class="flex justify-end">
      <button
        type="button"
        class=${buttonClass('primary')}
        ?disabled=${busy || steps.check.state === 'blocked'}
        @click=${() => void this.convert()}
      >
        ${this.spin('convert')} Translate & check
      </button>
    </div>`;
  }

  /**
   * Maps one template name to the value chosen in `select`, or (with '') removes its entry — the server takes the full
   * map. A refused choice is put back, so the select never shows what was not saved.
   */
  private mapName(select: HTMLSelectElement, name: string, before: string) {
    // Read now: the select is bound to the saved value, so a render while this waits puts that back.
    const ref = select.value;
    return this.act('mapping', async () => {
      try {
        await this.send(API.putMapping, { mapping: { ...(this.setup?.mapping ?? {}), [name.toLowerCase()]: ref || null } });
      } catch (err) {
        select.value = before;
        throw err;
      }
    });
  }

  /**
   * The template: its placeholders, each with the value it shows (a select over the full width). One that cannot be
   * filled says why, as a warning — it does not block. The names a PDF can use, click-to-copy, are folded away.
   */
  private templateCard(s: SetupView, steps: Steps) {
    if (!s.template || this.tplReplace) {
      return this.sourcePicker(
        'template',
        this.tplMode,
        this.tplLink,
        v => {
          this.tplLink = v;
        },
        s.template
          ? () => {
              this.tplReplace = false;
            }
          : undefined,
      );
    }
    const options = valueRefOptions(s);
    const chips = placeholderChips(
      s.template.inspection?.placeholders,
      s.mapping,
      options,
      s.template.inspection?.formFields ?? [],
    );
    const busy = !!this.busy;
    const chip = (name: string, field: boolean) =>
      html`<code
        class="rounded bg-(--sdr-sunken) px-1.5 py-0.5 font-mono text-xs"
        title=${field ? 'Form field' : nothing}
        >${field ? name : `{{${name}}}`}</code
      >`;
    return html`
      ${this.loadedFile('template', s.template, () => {
        this.tplReplace = true;
      })}
      ${this.germanLines(s.template.inspection?.warnings ?? [], 'warning')}
      ${steps.template.state === 'todo' ? this.stepProblem(steps.template.reason) : nothing}
      ${
        chips.length === 0
          ? html`<p class="text-sm text-(--sdr-ink-muted)">No placeholders found in this PDF.</p>`
          : html`<ul class="flex flex-col gap-2">
            ${chips.map(
              c => html`<li class="grid grid-cols-1 items-center gap-1.5 text-sm sm:grid-cols-[minmax(0,2fr)_minmax(0,3fr)] sm:gap-3">
                <span class="min-w-0 truncate">${chip(c.name, !!c.field)}</span>
                ${
                  c.state === 'unfillable'
                    ? html`<span class="${WARNING_CLASS} text-xs" lang="de" data-unfillable>${c.reason ?? 'Cannot be filled'}</span>`
                    : html`<select
                        class="${FIELD_CLASS.xs} w-full ${c.state === 'unmapped' ? 'border-(--color-warning)' : ''}"
                        aria-label="Value for ${c.field ? c.name : `{{${c.name}}}`}"
                        ?disabled=${busy}
                        .value=${liveValue(c.ref ?? '')}
                        @change=${(e: Event) => void this.mapName(e.target as HTMLSelectElement, c.name, c.ref ?? '')}
                      >
                        ${
                          // A name the server fills by itself gets no "Not set": removing its entry changes nothing (R35).
                          c.ref && fillsByName(c.name, options)
                            ? nothing
                            : html`<option value="" ?selected=${!c.ref}>${c.ref ? 'Not set' : 'Choose a value'}</option>`
                        }
                        ${options.map(o => html`<option value=${o.ref} ?selected=${o.ref === c.ref}>${o.label}</option>`)}
                      </select>`
                }
              </li>`,
            )}
          </ul>`
      }
      <details class=${FOLD_CLASS} data-placeholder-names>
        ${foldSummary('Placeholders for your PDF')}
        <div class="mt-2 flex flex-col gap-2">
          <p class="text-xs text-(--sdr-ink-subtle)">Write one into your PDF; click to copy it.</p>
          <div class="flex flex-wrap gap-1.5">
            ${options.map(
              o => html`<button
                type="button"
                class="rounded bg-(--sdr-sunken) px-1.5 py-0.5 font-mono text-xs text-(--sdr-ink-muted) transition-colors hover:bg-(--sdr-hover) hover:text-(--color-base-content)"
                title="Copy"
                @click=${() => void this.copyName(o.name)}
              >{{${o.name}}}</button>`,
            )}
          </div>
        </div>
      </details>
      <div class="flex justify-end">${this.nextButton('template', steps)}</div>
    `;
  }

  /** The example as it was made: its results (German, with units), the hook, and the PDF; faded, without the PDF, when `stale`. */
  private exampleResult(s: SetupView, example: NonNullable<SetupView['example']>, stale = false) {
    const shown = (s.outputs ?? []).filter(o => example.outputs?.[o.key] !== undefined);
    return html`<div
      class="flex flex-col gap-3 rounded-lg border border-(--sdr-line) p-3 transition-opacity ${stale ? 'opacity-60' : ''}"
      data-example
      data-stale=${stale ? 'true' : nothing}
    >
      ${
        shown.length
          ? html`<dl class="grid grid-cols-[minmax(0,1fr)_auto] gap-x-4 gap-y-1 text-sm">
              ${shown.map(
                o => html`<dt class="text-(--sdr-ink-muted)" lang="de">${labelWithoutUnit(o.label, unitOf(o))}</dt>
                  <dd class="text-right">${formatNumber(example.outputs?.[o.key], o)}</dd>`,
              )}
            </dl>`
          : nothing
      }
      ${example.hook ? html`<p class="text-sm italic" lang="de">${example.hook}</p>` : nothing}
      ${
        // A faded, outdated example offers no PDF: it would be taken for the current one.
        stale
          ? nothing
          : html`<a
              class="${buttonClass('plain', 'xs')} w-fit"
              href=${urlOf(API.examplePdf, s._id)}
              target="_blank"
              rel="noopener"
              >${icon({ name: 'document', size: 14 })} Open PDF</a
            >`
      }
    </div>`;
  }

  /**
   * The Example step. Approved: a calm summary — what was approved, and a quiet "Change example" (the header says
   * "Approved"). Otherwise the five values and the hook, the example below them, and one row of actions: "Create
   * example" is the main one until there is an example, and "Create again" only once a value or the hook changed or
   * the server calls the example outdated ("Looks good" is not offered for an example that no longer matches, R31);
   * else "Not right" and "Looks good". Changing an approved example shows only Cancel until something is changed.
   */
  private exampleCard(s: SetupView, steps: Steps) {
    const example = s.example;
    const approved = s.approval?.status === 'approved';
    if (approved && example && !this.editingApproved) {
      return html`${this.exampleResult(s, example)}
        <div class="flex justify-end">
          <button
            type="button"
            class=${buttonClass('quiet')}
            @click=${() => {
              this.editingApproved = true;
            }}
          >
            ${icon({ name: 'edit', size: 14 })} Change example
          </button>
        </div>`;
    }
    const savedHook = s.hook?.templates?.[0] ?? '';
    const hookValue = this.hookDraft ?? savedHook;
    // The example shows the saved hook; an edited one is saved only by the next "Create example" (R31).
    const hookChanged = this.hookDraft !== null && this.hookDraft.trim() !== savedHook.trim();
    const valuesChanged =
      !!example &&
      (s.inputs ?? []).some(i => {
        const typed = this.exampleDraft[i.key];
        return typed !== undefined && numberFrom(typed) !== (example.inputs?.[i.key] ?? i.example);
      });
    const changed = !!example && (hookChanged || valuesChanged);
    // The server's word that the example no longer matches the setup (its script, template or inputs changed).
    const outdated = !!example && steps.example.state === 'todo';
    const busy = !!this.busy;
    const feedback = this.feedback;
    const canJudge = !!example && !approved && !changed && !outdated;
    // Creating the same example again changes nothing (and on an approved one only drops the approval): it is
    // offered while there is none, after a change, or when the server says it is outdated — then as the main action.
    const offerCreate = !example || changed || outdated;
    const createButton = html`<button
      type="button"
      class=${buttonClass('primary')}
      ?disabled=${busy || steps.example.state === 'blocked'}
      aria-describedby=${changed ? 'assessment-example-changed' : nothing}
      @click=${() => void this.createExample()}
    >
      ${this.spin('example')} ${example ? 'Create again' : 'Create example'}
    </button>`;
    return html`
      ${s.approval?.status === 'changes-requested' ? this.stepProblem(steps.approved.reason ?? s.approval.feedback) : nothing}
      <div class="grid grid-cols-2 gap-3 sm:grid-cols-3">
        ${(s.inputs ?? []).map(
          i => html`<label class="flex min-w-0 flex-col justify-end gap-1 text-xs">
            <span class="text-(--sdr-ink-muted)" lang="de">${i.label}</span>
            <input
              class="${FIELD_CLASS.sm} w-full"
              type="number"
              step="any"
              name="example-${i.key}"
              .value=${this.exampleDraft[i.key] ?? String(example?.inputs?.[i.key] ?? i.example ?? '')}
              @input=${(e: Event) => {
                this.exampleDraft = { ...this.exampleDraft, [i.key]: (e.target as HTMLInputElement).value };
              }}
            />
          </label>`,
        )}
      </div>
      <label class="flex flex-col gap-1 text-xs">
        <span class="text-(--sdr-ink-muted)">Hook</span>
        <textarea
          class="${TEXTAREA_CLASS} min-h-16 [field-sizing:content]"
          name="hook"
          lang="de"
          rows="3"
          .value=${hookValue}
          @input=${(e: Event) => {
            this.hookDraft = (e.target as HTMLTextAreaElement).value;
          }}
        ></textarea>
      </label>
      ${example ? this.exampleResult(s, example, changed) : nothing}
      ${
        changed
          ? html`<p id="assessment-example-changed" class="text-xs text-(--sdr-ink-muted)">
              ${hookChanged ? 'Hook changed' : 'Values changed'} — create the example again.
            </p>`
          : nothing
      }
      ${
        canJudge && this.notRight
          ? html`
            <textarea
              class=${TEXTAREA_CLASS}
              name="feedback"
              rows="3"
              aria-label="What is not right?"
              placeholder="What is not right?"
              .value=${feedback}
              @input=${(e: Event) => {
                this.feedback = (e.target as HTMLTextAreaElement).value;
              }}
            ></textarea>
            <div class="flex flex-wrap justify-end gap-2">
              <button
                type="button"
                class=${buttonClass('quiet')}
                @click=${() => {
                  this.notRight = false;
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                class=${buttonClass('primary')}
                ?disabled=${!feedback.trim() || busy}
                @click=${() => void this.retranslate()}
              >
                ${this.spin('convert')} Translate again
              </button>
            </div>
          `
          : html`<div class="flex flex-wrap items-center justify-end gap-2">
              ${
                this.editingApproved && approved
                  ? html`<button
                      type="button"
                      class=${buttonClass('quiet')}
                      ?disabled=${busy}
                      @click=${() => {
                        this.editingApproved = false;
                        this.exampleDraft = {};
                        this.hookDraft = null;
                      }}
                    >
                      Cancel
                    </button>`
                  : nothing
              }
              ${
                canJudge
                  ? html`<button
                      type="button"
                      class=${buttonClass('quiet')}
                      ?disabled=${busy}
                      @click=${() => {
                        this.notRight = true;
                      }}
                    >
                      Not right
                    </button>`
                  : nothing
              }
              ${offerCreate ? createButton : nothing}
              ${
                canJudge
                  ? html`<button type="button" class=${buttonClass('primary')} ?disabled=${busy} @click=${() => void this.approve()}>
                      ${this.spin('approve')} Looks good
                    </button>`
                  : nothing
              }
            </div>`
      }
    `;
  }

  private readonly CARDS: Record<StepKey, (s: SetupView, steps: Steps) => unknown> = {
    calculator: (s, steps) => this.calculatorCard(s, steps),
    fields: (s, steps) => this.fieldsCard(s, steps),
    check: (s, steps) => this.checkCard(s, steps),
    template: (s, steps) => this.templateCard(s, steps),
    example: (s, steps) => this.exampleCard(s, steps),
  };

  // ── The page ──────────────────────────────────────────────────────────
  /** The last action's refusal, in every view with an action — act() puts it nowhere else (R34, N1). */
  private refusalLine() {
    const refusal = this.refusal;
    return refusal
      ? html`<p class="text-sm text-(--color-error)" role="alert" lang=${refusal.german ? 'de' : nothing}>${refusal.text}</p>`
      : nothing;
  }

  /** Why the blocked step just clicked did not open — under the stepper, where the click was; the reason is German. */
  private blockedStepLine() {
    const { label, german } = this.blockedStep;
    if (!label || !this.stepNotice.text) return nothing;
    return html`<p class="flex items-start gap-1.5 text-xs text-(--sdr-ink-muted)" role="status" data-step-why>
      <span class="mt-px flex text-(--sdr-ink-subtle)">${icon({ name: 'lock', size: 14 })}</span>
      <span><span class="font-medium text-(--color-base-content)">${label}</span> ·
        <span lang=${german ? 'de' : nothing}>${this.stepNotice.text}</span></span
      >
    </p>`;
  }

  /** What a toast said before: a short line on the card, for a few seconds. */
  private noticeLine() {
    return this.notice.text
      ? html`<p class="text-sm text-(--sdr-ink-muted)" role="status" data-notice>${this.notice.text}</p>`
      : nothing;
  }

  /**
   * The question before a change removes the approval (R33): "Remove approval" or Cancel. It opens at the top of the
   * card and stays in view (sticky) wherever on a long card the change was made; the rest of the card waits, dimmed.
   */
  private askPanel() {
    const open = this.asking;
    if (!open) return nothing;
    return html`<div
      data-ask
      role="alertdialog"
      aria-labelledby="assessment-ask-title"
      aria-describedby="assessment-ask-text"
      class="sticky top-4 z-10 flex flex-col gap-3 rounded-lg border border-(--sdr-line-strong) bg-(--sdr-sunken) p-3 sm:flex-row sm:items-center"
      @keydown=${(e: KeyboardEvent) => {
        if (e.key === 'Escape') {
          e.preventDefault();
          this.answer(false);
        }
      }}
    >
      <div class="flex-1">
        <p id="assessment-ask-title" class="text-sm font-medium">${REVOKE_TITLE}</p>
        <p id="assessment-ask-text" class="text-sm text-(--sdr-ink-muted)">${open.text}</p>
      </div>
      <div class="flex justify-end gap-2">
        <button type="button" class=${buttonClass('quiet')} @click=${() => this.answer(false)}>Cancel</button>
        <button type="button" data-continue class=${buttonClass('primary')} @click=${() => this.answer(true)}>
          Remove approval
        </button>
      </div>
    </div>`;
  }

  private shell(body: unknown, status: unknown = nothing): TemplateResult {
    return html`
      <div class="mx-auto flex max-w-2xl flex-col gap-6 px-4 py-6 sm:px-6">
        <div class="flex flex-col gap-1">
          <div class="flex items-center justify-between gap-3">
            <h1 class="text-[2rem] leading-10 font-medium tracking-tight">Assessment</h1>
            ${status}
          </div>
          <p class="text-sm text-(--sdr-ink-muted)">A business case PDF per lead, from your own calculator.</p>
        </div>
        ${body}
      </div>
    `;
  }

  override render() {
    const busy = !!this.busy;
    if (this.enabled === null || (this.enabled && this.setups === null && !this.loadError)) {
      return this.shell(html`<div class="flex justify-center py-16 text-(--sdr-ink-subtle)" role="status" aria-label="Loading">
        ${spinner('md')}
      </div>`);
    }
    if (!this.enabled) {
      return this.shell(html`<div class="${CARD_CLASS} p-6 text-sm">
        The Assessment Agent is not enabled for this account.
      </div>`);
    }
    if (this.loadError) {
      return this.shell(html`<div
        class="flex items-center gap-3 rounded-xl border border-(--color-error) bg-(--color-base-100) px-4 py-3 text-sm"
        role="alert"
      >
        <span class="flex-1 text-(--color-error)">${this.loadError}</span>
        <button type="button" class=${buttonClass('quiet', 'xs')} @click=${() => void this.loadSetups()}>Try again</button>
      </div>`);
    }
    const setup = this.setup;
    if (!setup) {
      // An empty state carries no icon (I7).
      return this.shell(html`
        <div class="${CARD_CLASS} flex flex-col items-center gap-4 p-8 text-center">
          <div>
            <h2 class="text-[0.9375rem] font-medium">No setup yet</h2>
            <p class="text-sm text-(--sdr-ink-muted)">Use your own calculator, or try the sample first.</p>
          </div>
          <div class="flex flex-wrap justify-center gap-2">
            <button type="button" class=${buttonClass('plain')} ?disabled=${busy} @click=${() => void this.create(true)}>
              ${this.spin('sample')} Start with the sample
            </button>
            <button type="button" class=${buttonClass('primary')} ?disabled=${busy} @click=${() => void this.create(false)}>
              ${this.spin('scratch')} Start from scratch
            </button>
          </div>
          ${this.refusalLine()}
        </div>
      `);
    }

    const steps = readSteps(setup.steps);
    const step = this.step;
    const label = SETUP_STEPS.find(s => s.key === step)?.label ?? '';
    const blockedReason = steps[step].state === 'blocked' ? steps[step].reason : undefined;
    // An approved setup's Example step says so where the help line is, at the top of the card.
    const showsApproved =
      step === 'example' && setup.approval?.status === 'approved' && !!setup.example && !this.editingApproved;
    const setups = this.setups ?? [];

    return this.shell(
      html`
        ${
          setups.length > 1
            ? html`<select
              class="${FIELD_CLASS.sm} w-full sm:w-64"
              aria-label="Setup"
              @change=${(e: Event) => this.chooseSetup((e.target as HTMLSelectElement).value)}
            >
              ${setups.map(s => html`<option value=${s._id} ?selected=${s._id === setup._id}>${s.name || 'Untitled setup'}</option>`)}
            </select>`
            : nothing
        }
        <div class="flex flex-col gap-2">
          ${microStepper({
            items: stepperItems(steps, step),
            label: 'Setup steps',
            onSelect: key => {
              // As Next: a wrong range on screen is not left behind unseen (it is not saved).
              if (step === 'fields' && key !== 'fields' && this.hasUnsavedRange()) {
                this.blockedStep = { label: SETUP_STEPS.find(s => s.key === key)?.label ?? '', german: false };
                this.stepNotice.show(UNSAVED_RANGE, 6000);
                return;
              }
              this.goTo(key as StepKey);
            },
            onBlocked: item => {
              this.blockedStep = { label: item.label, german: !!item.reason };
              this.stepNotice.show(item.reason || 'Finish the steps before it first.', 6000);
            },
          })}
          ${this.blockedStepLine()}
        </div>
        <section class=${CARD_CLASS} aria-labelledby="assessment-step-title">
          <div class="ndi-step-enter flex flex-col gap-4 p-4 sm:p-6">
            <div class="flex flex-col gap-0.5">
              <h2 id="assessment-step-title" class="text-[0.9375rem] font-medium">${label}</h2>
              ${
                blockedReason
                  ? html`<p class="text-sm text-(--sdr-ink-muted)" lang="de">${blockedReason}</p>`
                  : showsApproved
                    ? html`<p class="flex items-center gap-1.5 text-sm" role="status" data-approved>
                        <span class="flex text-(--color-success)">${icon({ name: 'check', size: 16 })}</span>
                        Approved — start assessments from a lead.
                      </p>`
                    : html`<p class="text-sm text-(--sdr-ink-muted)">${HELP[step]}</p>`
              }
            </div>
            ${this.askPanel()}
            <div
              class="flex flex-col gap-4 transition-opacity ${this.asking ? 'opacity-50' : ''}"
              ?inert=${!!this.asking}
              data-step-body
            >
              ${this.CARDS[step](setup, steps)}
            </div>
            ${this.refusalLine()}
            ${this.noticeLine()}
          </div>
        </section>
      `,
      toneDot(approvalLook(setup.approval?.status)),
    );
  }
}

if (typeof customElements !== 'undefined' && !customElements.get('sdr-assessment-page')) {
  customElements.define('sdr-assessment-page', SdrAssessmentPage);
}

declare global {
  interface HTMLElementTagNameMap {
    'sdr-assessment-page': SdrAssessmentPage;
  }
}

/** The page, as the router places it (`AssessmentPage({})`); `pollMs` for tests. */
export function AssessmentPage(p: { pollMs?: number } = {}): TemplateResult {
  return html`<sdr-assessment-page .pollMs=${p.pollMs ?? 2000}></sdr-assessment-page>`;
}
